import React, { useState, useEffect, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import axios from 'utils/api/axios'
import AxiosUtils from 'utils/api/axiosUtils'
import { Message, MessageSeverity } from 'constants/message'
import TaskCellSituation from './components/taskCellSituation'
import WP_Constants from '../wpConstants'
import { HTTP_CODES } from 'constants/constants'
import './widgetListTasks.css'
import { Link, Pagination, Table, TableBody, TableCell, TableHead, TableRow } from '@airbus/components-react/components'
import LangContext from 'context/LangContext'
import MessageContext from 'context/MessageContext'
import LoadingContext from 'context/LoadingContext'
import UrlApis from 'utils/api/urlApis'
import AuthContext from 'context/AuthContext'
import LocalStorage from 'utils/myLocalStorage'
import ComponentsUNames from 'constants/componentsUNames'
import Utils from 'constants/config'

const CODE_TYPE_OE = '4'

const WidgetListTasks = ({ pageUniqueName, bdvnumdoc, summary }) => {
  const { t } = useTranslation()
  const baseUrl = process.env.PUBLIC_URL
  const navigate = useNavigate()

  const hasWoPermissions = LocalStorage.getOrDefault(
    [LocalStorage.KEY_PERMISSIONS, 'AMSWEB_ACCESS_MAOE-DSP'],
    LocalStorage.VALUE_TYPE.BOOLEAN,
    false,
  )

  const [data, setData] = useState()
  const [isLoadInProgressActive, setIsLoadInProgressActive] = useState(false)

  const { lang } = useContext(LangContext)
  const { handleDisplayMessage } = useContext(MessageContext)
  const { handleLoadInProgress } = useContext(LoadingContext)
  const { handleRequestLogin } = useContext(AuthContext)

  const [pagination, setPagination] = useState({
    nbItems: 0,
    pageSize: LocalStorage.getOrDefault(
      [
        LocalStorage.KEY_PREFS,
        ComponentsUNames.BDV_STORE_NAME,
        ComponentsUNames.BDV_WDG_LIST_TASKS_UNAME + '|' + ComponentsUNames.PROP_PAGE_SIZE,
      ],
      LocalStorage.VALUE_TYPE.INTEGER,
      Utils.PAGINATION_DEFAULT_PAGESIZE,
    ),
    pageNo: 0,
    nbPages: 0,
    choiceNbRows: [10, 20, 50],
  })

  /********
   * Synchronization of widget's data to keep track of them
   ********/

  const loadData = async (firstPage = false) => {
    try {
      if (!summary) return

      const endpointToCall = [
        WP_Constants.ADDWRK_OPENED,
        WP_Constants.ADDWRK_CLOSED,
        WP_Constants.TOTAL_TS,
        WP_Constants.ADDWRK_DEFERRED,
        WP_Constants.ADDWRK_WITHTS,
        WP_Constants.ADDWRK_FROMTS,
        WP_Constants.OPERATIONS_TS,
        WP_Constants.OPERATIONS_NOOP_TS,
        WP_Constants.OPERATIONS_OOP_TS,
        WP_Constants.WORKORDERS_TS,
        WP_Constants.WORKORDERS_TL_TS,
        WP_Constants.WORKORDERS_MC_TS,
        WP_Constants.WORKORDERS_DOC_TS,
        WP_Constants.WORKORDERS_KX_TS,
        WP_Constants.WORKORDERS_MIS_TS,
        WP_Constants.TOTAL_TS,
      ].includes(summary)
        ? UrlApis.URL_WPSTATUS_LIST_TASKS_AW_GET
        : UrlApis.URL_WPSTATUS_LIST_TASKS_WO_GET

      const headerCompleted = await AxiosUtils.getHeader(AxiosUtils.HEADERS.HEADER_READ)
      const response = await axios.get(
        encodeURI(
          endpointToCall +
            '?lang=' +
            lang +
            '&bdvnumdoc1=' +
            bdvnumdoc.substring(0, 4) +
            '&bdvnumdoc2=' +
            bdvnumdoc.substring(4) +
            '&key=' +
            summary +
            '&size=' +
            pagination.pageSize +
            '&page=' +
            (firstPage ? 0 : pagination.pageNo),
        ),
        {},
        {
          headers: headerCompleted,
          withCredentials: false,
        },
      )

      // Init widget locally
      console.debug('data retrieved <' + t('9500019') + '>')
      setData(response?.data.content)

      setPagination({
        ...pagination,
        nbItems: response?.data.totalElements,
        nbPages: response?.data.totalPages,
        pageNo: response?.data.number,
        pageSize: response?.data.size,
      })
    } catch (err) {
      console.debug('ERR <' + t('9500019') + '>: ' + err)
      if (err.response.status === HTTP_CODES.HTTP_CODE_SESSION_EXPIRED) {
        handleDisplayMessage(new Message(MessageSeverity.WARNING, t('9500019'), null, t('sessionExpired')))
        handleRequestLogin()
      } else if (err.response.status === HTTP_CODES.HTTP_CODE_NOT_FOUND) {
        handleDisplayMessage(new Message(MessageSeverity.ERROR, t('9500019'), null, t('9500173')))
      } else {
        handleDisplayMessage(
          new Message(
            MessageSeverity.ERROR,
            t('9500092'),
            err.response.data?.amasisCode,
            err.response.data?.description
              ? err.response.data?.description
              : err.response.data.title
                ? err.response.data.title
                : err.response.data.message
                  ? err.response.data.message
                  : err.response.data.error,
          ),
        )
      }
    } finally {
      handleLoadInProgress(false)
      setIsLoadInProgressActive(false)
    }
  }

  const handleOpenWo = (e) => {
    const urlToNavigate = baseUrl + '/workpackage/wo/' + e.currentTarget.text
    navigate(urlToNavigate)
  }

  useEffect(() => {
    if (bdvnumdoc !== undefined && summary !== undefined) {
      handleLoadInProgress(true)
      setIsLoadInProgressActive(true)
      loadData(true)
    } else {
      setData(null)
    }
  }, [bdvnumdoc, summary])

  useEffect(() => {
    if (bdvnumdoc !== undefined && summary !== undefined) {
      handleLoadInProgress(true)
      setIsLoadInProgressActive(true)
      loadData()
    }
  }, [lang, pagination.pageSize, pagination.pageNo])

  return (
    <div className='WidgetWpListTasks'>
      {data && JSON.stringify(data) !== '{}' && JSON.stringify(data) !== '[]' ? (
        <div>
          <colgroup>
            <col style={{ width: '4%' }} />
            <col style={{ width: '9%' }} />
            <col style={{ width: '16%' }} />
            <col style={{ width: '12%' }} />
            <col style={{ width: '5%' }} />
            <col style={{ width: '5%' }} />
            <col style={{ width: '6%' }} />
            <col style={{ width: '7%' }} />
            <col style={{ width: '7%' }} />
            <col style={{ width: '9%' }} />
            <col style={{ width: '11%' }} />
          </colgroup>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('7000841')}</TableCell>
                <TableCell>{t('9500115')}</TableCell>
                <TableCell>{t('1LEX071')}</TableCell>
                <TableCell style={{ maxWidth: '20em' }}>{t('0P00003')}</TableCell>
                <TableCell>{t('0EKS004')}</TableCell>
                <TableCell align='right'>{t('7000494')}</TableCell>
                <TableCell align='right'>{t('9200107')}</TableCell>
                <TableCell>{t('9500035')}</TableCell>
                <TableCell>{t('1MOE023')}</TableCell>
                <TableCell>{t('1P00399')}</TableCell>
                <TableCell>{t('0MRM071')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data ? (
                data.map((d, i) => (
                  <TableRow key={'wpstatus_list_tasks_' + i}>
                    <TableCell style={{ textWrap: 'nowrap', textOverflow: 'initial' }}>
                      {d.hasOwnProperty('numord') ? d.numord : '-'}
                    </TableCell>
                    <TableCell style={{ textWrap: 'nowrap', textOverflow: 'initial' }}>
                      {d.hasOwnProperty('type') ? d.type : '-'}
                    </TableCell>
                    <TableCell style={{ textWrap: 'nowrap', textOverflow: 'initial' }}>
                      {d.hasOwnProperty('document') ? (
                        d.codeType === CODE_TYPE_OE ? (
                          <Link variant='underlined' size='small' onClick={handleOpenWo} disabled={!hasWoPermissions}>
                            {d.document}
                          </Link>
                        ) : (
                          d.document
                        )
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      <div className='table_data_truncate_1_line'>{d.hasOwnProperty('titre') ? d.titre : '-'}</div>
                    </TableCell>
                    <TableCell>{d.hasOwnProperty('ata') ? d.ata : '-'}</TableCell>
                    <TableCell align='right'>{d.hasOwnProperty('zone') ? d.zone : '-'}</TableCell>
                    <TableCell align='right'>{d.hasOwnProperty('reTs') ? d.reTs : '-'}</TableCell>
                    <TableCell>
                      <TaskCellSituation oneData={d} />
                    </TableCell>
                    <TableCell>{d.hasOwnProperty('dateExec') ? d.dateExec : '-'}</TableCell>
                    <TableCell>{d.hasOwnProperty('reportTs') ? d.reportTs : '-'}</TableCell>
                    <TableCell>
                      {d.hasOwnProperty('noWo') ? (
                        <Link variant='underlined' size='small' onClick={handleOpenWo} disabled={!hasWoPermissions}>
                          {d.noWo}
                        </Link>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow />
              )}
            </TableBody>

            <TableRow>
              <TableCell colSpan='11'>
                <Pagination
                  count={pagination.nbItems}
                  page={pagination.pageNo + 1}
                  onPageChange={(e, newPage) => setPagination({ ...pagination, pageNo: newPage - 1 })}
                  rowsPerPage={pagination.pageSize}
                  onRowsPerPageChange={(e, newRowPerPage) => {
                    LocalStorage.put(
                      [LocalStorage.KEY_PREFS, pageUniqueName, ComponentsUNames.BDV_WDG_LIST_TASKS_UNAME + '|PAGESIZE'],
                      newRowPerPage,
                    )
                    setPagination({ ...pagination, pageSize: parseInt(newRowPerPage), pageNo: 0 })
                  }}
                  rowsPerPageOptions={pagination.choiceNbRows}
                  showRowsPerPage
                  rowsPerPageLabel={t('entries_per_page')}
                />
              </TableCell>
            </TableRow>
          </Table>
        </div>
      ) : (
        <div style={{ paddingLeft: '1em' }}>{isLoadInProgressActive ? t('9500166') : t('9500055')}</div>
      )}
    </div>
  )
}

export default WidgetListTasks
